"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { ThermometerSun, Droplets, Check, ArrowRight } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const services = [{
  title: "Infrared Salt Sauna Therapy",
  description: "Experience deep, cellular-level detoxification and profound relaxation in our state-of-the-art infrared saunas.",
  icon: ThermometerSun,
  benefits: ["Reduces cortisol levels", "Relieves muscle tension", "Improves sleep quality"],
  link: "/ServicesPage"
}, {
  title: "Vitamin Injections + Peptides\xA0",
  description: "Experience targeted vitamin and peptide therapies to boost energy, support immunity, and enhance overall wellness.",
  icon: Droplets,
  benefits: ["Instant hydration", "Boosts energy levels", "Supports immune function", "Personalized wellness plan"],
  link: "/ServicesPage"
}, {
  title: "Colon Hydrotherapy",
  description: "A gentle internal cleansing process that supports digestive health and promotes overall systemic detoxification.",
  icon: Droplets,
  benefits: ["Supports digestive health", "Enhances nutrient absorption", "Promotes internal balance"],
  link: "/ServicesPage"
}];
export default function StressReliefServices() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="stressreliefservices" className="relative bg-background py-24 lg:py-32 overflow-hidden">
      {}
      <div className="absolute inset-0 bg-gradient-to-b from-background via-secondary/10 to-background pointer-events-none" />

      <div className="container relative mx-auto px-4 md:px-6">
        <div ref={headerRef} className={`max-w-3xl mx-auto text-center mb-16 lg:mb-24 transition-all duration-1000 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="font-default text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-foreground mb-6">
            Curated Stress Relief
          </h2>
          <p className="font-default text-lg text-muted-foreground leading-relaxed">
            Discover our specialized protocols designed to restore balance, alleviate tension, and elevate your overall well-being in a serene, luxury environment.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10">
          {services.map((service, index) => {
          const {
            ref: cardRef,
            isVisible: isCardVisible
          } = useRevealOnScroll({
            threshold: 0.1
          });
          return <div key={service.title} data-index={index} ref={cardRef} className={`transition-all duration-1000 ease-out ${isCardVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`} style={{
            transitionDelay: `${index * 150}ms`
          }}>
                <Card className="h-full flex flex-col bg-background/60 backdrop-blur-md border-border/40 shadow-sm hover:shadow-xl hover:scale-[1.02] hover:bg-background/80 transition-all duration-500 group rounded-2xl overflow-hidden">
                  <CardHeader className="pb-4 pt-8 px-8">
                    <div className="mb-6 inline-flex items-center justify-center size-14 rounded-lg bg-secondary/50 text-foreground group-hover:bg-foreground group-hover:text-background transition-colors duration-500">
                      <service.icon strokeWidth={1.25} className="size-7" />
                    </div>
                    <CardTitle className="font-default text-2xl font-medium tracking-tight mb-3">
                      {service.title}
                    </CardTitle>
                    <CardDescription className="font-default text-base leading-relaxed text-muted-foreground">
                      {service.description}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="px-8 pb-8 flex-grow">
                    <Separator className="mb-6 opacity-50" />
                    <ul className="space-y-4">
                      {service.benefits.map((benefit, i) => <li key={i} className="flex items-start gap-3">
                          <Check strokeWidth={2} className="size-4 mt-1 text-foreground/40 shrink-0" />
                          <span className="font-default text-sm text-muted-foreground leading-relaxed">
                            {benefit}
                          </span>
                        </li>)}
                    </ul>
                  </CardContent>

                  <CardFooter className="px-8 pb-8 pt-0 mt-auto">
                    <Button asChild variant="ghost" className="w-full justify-between group/btn hover:bg-secondary/50 text-foreground font-medium">
                      <Link to={service.link}>
                        Explore Protocol
                        <ArrowRight className="size-4 ml-2 transition-transform duration-300 group-hover/btn:translate-x-1" />
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </div>;
        })}
        </div>

        <div className="mt-16 lg:mt-24 text-center">
          <Button asChild size="lg" className="rounded-lg px-8 h-12 text-base bg-[#8FA88F] hover:bg-[#8FA88F]/90 text-white">
            <Link to="/BookNowPage">Book Your Session</Link>
          </Button>
        </div>
      </div>
    </section>;
}